/**
 * apiFetch — central fetch wrapper that automatically injects Authorization Bearer token.
 *
 * Two exports:
 *   apiFetch(path, options)    → calls the DB API  (REACT_APP_DB_API_URL  / :5000)
 *   aiApiFetch(path, options)  → calls the AI API  (REACT_APP_API_URL     / :4000)
 *
 * Both automatically attach:
 *   - Authorization: Bearer <JWT>  (retrieved from sessionStorage via getAuthToken())
 *   - Content-Type: application/json  (when a body is present)
 *
 * SECURITY NOTE:
 *   The previous implementation injected a static x-api-key from a REACT_APP_* env var,
 *   which was bundled into the client-side JS bundle and visible in DevTools.
 *   This has been replaced with short-lived JWT Bearer tokens that are:
 *     - Issued server-side on login
 *     - Stored in sessionStorage (not localStorage)
 *     - Expire after 8 hours
 *     - Invalidated server-side on logout
 */

const DB_API_BASE = process.env.REACT_APP_DB_API_URL;
const AI_API_BASE = process.env.REACT_APP_API_URL;

/**
 * Retrieves the current JWT from sessionStorage.
 * Returns null if no token is present or if the token is expired.
 */
function getAuthToken() {
  try {
    const token = sessionStorage.getItem('iassess_token');
    if (!token) return null;

    // Basic expiry check without importing a full JWT library
    const parts = token.split('.');
    if (parts.length !== 3) return null;

    const payload = JSON.parse(atob(parts[1].replace(/-/g, '+').replace(/_/g, '/')));
    if (payload.exp && Date.now() >= payload.exp * 1000) {
      // Token expired — clean up storage
      sessionStorage.removeItem('iassess_token');
      sessionStorage.removeItem('iassess_user');
      sessionStorage.removeItem('iassess_auth_mode');
      return null;
    }

    return token;
  } catch {
    return null;
  }
}

function buildHeaders(options) {
  const token = getAuthToken();
  return {
    ...(options.body ? { 'Content-Type': 'application/json' } : {}),
    ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    ...(options.headers || {}),
  };
}

export default async function apiFetch(path, options = {}) {
  return fetch(`${DB_API_BASE}${path}`, {
    ...options,
    headers: buildHeaders(options),
  });
}

export async function aiApiFetch(path, options = {}) {
  return fetch(`${AI_API_BASE}${path}`, {
    ...options,
    headers: buildHeaders(options),
  });
}
